import { get, put, post, getText } from '../lib/ajax';
import {
    ICoverageModel,
    IStore,
    ISubmitResponse,
    IUpdateOfferPayload,
    IUpdateOfferResponse,
    IUpdateOfferData,
    IFormStoreCoverageModel,
    InsuranceType,
    ISubmitData
} from '../type';
import {
    selectEndpoints,
    selectOfferNumber,
    selectPrimaryType,
    selectCurrentInsurance,
    selectCustomerInfoValues,
    selectPaymentInfoValues,
    selectAccidentObjectId,
    selectObjectId,
    selectProductName,
    selectResources
} from './selectors';
import { formatPayloadDate } from '../lib/date';

const getPolicyType = (primaryType: InsuranceType) => (primaryType === 'accident' ? 'ACCIDENT_YOUTH' : 'CONTENT_YOUTH');

export const getResources = (store: IStore) => get(selectEndpoints(store).resources);

export const getContentBlocks = (store: IStore, path: string) =>
    getText(`${selectEndpoints(store).contentBlocks}${path}`);

export const createOffer = (store: IStore): Promise<ICoverageModel> => {
    const { url, options } = selectEndpoints(store).createOffer;

    return get(`${url}/${getPolicyType(selectPrimaryType(store))}`, options);
};

export const updateOffer = (store: IStore, data: IUpdateOfferData): Promise<IUpdateOfferResponse> => {
    const offerNumber = selectOfferNumber(store);

    if (!offerNumber) throw new Error('Missing offerNumber in state');

    const { url, options } = selectEndpoints(store).updateOffer;
    const payload: IUpdateOfferPayload = {
        offerNumber
    };

    if (data.disabilityCoverageAmount) {
        const objectId = selectAccidentObjectId(store);

        if (!objectId) throw new Error('Missing objectId in state');

        payload.objectId = objectId;
        payload.disabilityCoverageAmount = data.disabilityCoverageAmount;
    } else {
        const objectId = selectObjectId(store);

        if (!objectId) throw new Error('Missing objectId in state');

        payload.objectId = objectId;
        payload.selectedCoverageIds =
            data.selectedCoverages &&
            data.selectedCoverages.map(coverage => {
                if (!payload.policyOfferId) {
                    payload.policyOfferId = coverage.policyOfferId;
                }

                return coverage.objectCoverageId;
            });
    }

    return put(`${url}/${getPolicyType(selectPrimaryType(store))}`, payload, options);
};

export const getReceipt = (store: IStore) => {
    const { url, options } = selectEndpoints(store).getReceipt;
    return get(url, options);
};

export const submitOffer = (store: IStore, model: IFormStoreCoverageModel): Promise<ISubmitResponse> => {
    const { url, options } = selectEndpoints(store).submitOffer;
    const currentInsurance = selectCurrentInsurance(store);
    const customer = selectCustomerInfoValues(store);
    const payment = selectPaymentInfoValues(store);
    const primaryType = selectPrimaryType(store);
    const objectId = selectObjectId(store);
    const productData = primaryType === 'accident' ? currentInsurance.accident : currentInsurance.contents;
    const productName = selectProductName(store, primaryType);
    const resources = selectResources(store);

    if (!objectId) {
        throw new Error('Missing objectId');
    }

    if (!productName) {
        throw new Error('Missing productName');
    }

    const data: ISubmitData = {
        objectId,
        offerNumber: model.offerNumber,
        dealPrice: model.dealPrice,
        disabilityCoverageAmount: model.disabilityCoverageAmount,
        coverages: {
            accident: model.coverages.accident.filter(({ chosen }) => chosen),
            contents: model.coverages.contents.filter(({ chosen }) => chosen)
        },
        items: [
            {
                objectId,
                activationDate: formatPayloadDate(currentInsurance.newInsuranceStartDate),
                productData: productData.shouldTerminate
                    ? {
                          productName,
                          companyName: productData.currentInsuranceCompanyName,
                          discontinuationDate: formatPayloadDate(productData.terminationDate)
                      }
                    : undefined
            }
        ],
        customer: {
            name: customer.name,
            cpr: customer.cprNumber,
            address: customer.address,
            mobilePhone: customer.mobile,
            email: customer.email
        },
        payment: {
            regNo: payment.regNum,
            accountNo: payment.accountNum,
            customerIsAccountHolder: !payment.isSomeoneElseAccount,
            accountHolderCPR: payment.accountHolderCprNumber || undefined
        },
        replyEmail: resources.replyEmail,
        customerEmailSubject: resources.customerEmailSubject,
        businessMailAddress: resources.businessMailAddress,
        businessEmailSubject: resources.businessEmailSubject
    };

    return post(url, data, options);
};
